import { versionAsset } from "../assets/revision.js";
import type { Cue, MusicScene } from "./score.js";

/**
 * The rendered music (D97): four scene loops and the stingers, composed as code in
 * `packages/client/music/compose.mjs`, rendered by rustsynth and shipped as Ogg Vorbis under
 * `/assets/music/` with a manifest. The browser synth in score.ts stays the fallback for every
 * piece the manifest does not have or the network does not deliver, so the game is never silent.
 */
export interface MusicManifest {
  version: number;
  loops: Partial<Record<MusicScene, { file: string; seconds: number; bars: number; bpm: number; title: string }>>;
  cues: Partial<Record<Cue, { file: string; seconds: number; title: string }>>;
}

export const MUSIC_ROOT = "/assets/music/";

/** A loop is one file exactly one period long; the tail of its last bar already sits at its head. */
export function musicUrl(file: string): string {
  return versionAsset(`${MUSIC_ROOT}${file}`);
}

export function parseManifest(value: unknown): MusicManifest | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  if (record["version"] !== 1 || typeof record["loops"] !== "object" || typeof record["cues"] !== "object") return null;
  return { version: 1, loops: (record["loops"] ?? {}) as MusicManifest["loops"], cues: (record["cues"] ?? {}) as MusicManifest["cues"] };
}

/** Decoded audio, kept only as far as memory allows: one loop and a handful of the latest cues. */
export class MusicLibrary {
  private manifest: Promise<MusicManifest | null>;
  private known: MusicManifest | null = null;
  private loops = new Map<string, Promise<AudioBuffer>>();
  private cues = new Map<string, Promise<AudioBuffer>>();
  private ready = new Map<string, AudioBuffer>();
  constructor(private readonly context: BaseAudioContext, private readonly fetchImpl: typeof fetch = (input, init) => fetch(input, init), private readonly cueCache = 8) {
    this.manifest = this.fetchImpl(musicUrl("manifest.json"))
      .then((response) => (response.ok ? response.json() : null))
      .then((json) => parseManifest(json))
      .catch(() => null)
      .then((manifest) => { this.known = manifest; return manifest; });
  }

  /** A cue's stinger if it is decoded already — the answer a click cannot wait for. */
  peekCue(cue: Cue): AudioBuffer | null {
    const file = this.known?.cues[cue]?.file;
    return file ? this.ready.get(file) ?? null : null;
  }

  /** Decodes the cues a session is most likely to need, one after another, so their first play is the real piece. */
  async warm(cues: readonly Cue[]): Promise<void> {
    for (const cue of cues) await this.cue(cue).catch(() => null);
  }

  /** The decoded loop for a scene, or null when there is none; every other scene's loop is let go. */
  async loop(scene: MusicScene): Promise<AudioBuffer | null> {
    const manifest = await this.manifest;
    const entry = manifest?.loops[scene];
    if (!entry) return null;
    for (const file of [...this.loops.keys()]) if (file !== entry.file) this.loops.delete(file);
    let pending = this.loops.get(entry.file);
    if (!pending) {
      pending = this.decode(entry.file);
      this.loops.set(entry.file, pending);
      pending.catch(() => this.loops.delete(entry.file));
    }
    return pending;
  }

  /** The decoded stinger for a cue, or null when the cue has no rendered piece. */
  async cue(cue: Cue): Promise<AudioBuffer | null> {
    const manifest = await this.manifest;
    const entry = manifest?.cues[cue];
    if (!entry) return null;
    let pending = this.cues.get(entry.file);
    if (pending) { this.cues.delete(entry.file); this.cues.set(entry.file, pending); return pending; }
    pending = this.decode(entry.file);
    this.cues.set(entry.file, pending);
    pending.catch(() => this.cues.delete(entry.file));
    for (const file of [...this.cues.keys()]) if (this.cues.size > this.cueCache) { this.cues.delete(file); this.ready.delete(file); } else break;
    return pending;
  }

  /** Whether the manifest lists a rendered piece for a cue, without decoding anything. */
  async has(cue: Cue): Promise<boolean> {
    return !!(await this.manifest)?.cues[cue];
  }

  /** Which pieces the manifest offers and which stingers are decoded, for the debug probe and the browser proof. */
  async describe(): Promise<{ loops: string[]; cues: string[]; ready: string[] } | null> {
    const manifest = await this.manifest;
    if (!manifest) return null;
    const ready = Object.entries(manifest.cues).filter(([, entry]) => entry && this.ready.has(entry.file)).map(([cue]) => cue);
    return { loops: Object.keys(manifest.loops), cues: Object.keys(manifest.cues), ready };
  }

  private async decode(file: string): Promise<AudioBuffer> {
    const response = await this.fetchImpl(musicUrl(file));
    if (!response.ok) throw new Error(`music ${file}: ${response.status}`);
    const bytes = await response.arrayBuffer();
    const buffer = await this.context.decodeAudioData(bytes);
    this.ready.set(file, buffer);
    return buffer;
  }
}
